import { Pressable, StyleSheet, Text, View } from "react-native";
import { Trans, useTranslation } from "react-i18next";
import { coerceLocale, muscleKey, pickLocalizedText } from "@heraion/shared";

import { joinList } from "../joinList";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { useDisclosure } from "../primitives/useDisclosure";
import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import type { GapCandidateView, RowGapProposal } from "./muscleCoverageProposal";
import type { ProgressViewer } from "./types";

// AP-9, variant A of next/ap9-volume-gap-mockup.html (Antenore's choice): one
// 44pt line under a row below MEV, closed by default; open, the exercises that
// close the gap, each alone, with its sets. Read-only: nothing here acts
// (adding to the plan is AP-4). No reps: nothing sources them (§17b.2bis).

export type MuscleGapProposalProps = {
  theme: UiTheme;
  proposal: RowGapProposal;
  /** The row's muscle name, already translated. */
  muscleLabel: string;
  /** Sets in the reader's locale, the row's own formatter. */
  formatSets: (v: number) => string;
  locale: string;
  viewer: ProgressViewer;
};

export function MuscleGapProposal({
  theme,
  proposal,
  muscleLabel,
  formatSets,
  locale,
  viewer,
}: MuscleGapProposalProps) {
  const { t, i18n } = useTranslation();
  const { expanded, toggle, a11yProps } = useDisclosure(false);
  const styles = makeStyles(theme);

  if (proposal.kind === "shortHistory") {
    return (
      <Text style={styles.note}>
        {t("progress.gapProposal.shortHistory", {
          count: proposal.fullWeeks,
          n: proposal.fullWeeks,
        })}
      </Text>
    );
  }

  const gap = formatSets(proposal.gap);
  const lang = coerceLocale(i18n.language);
  // Names only: the weight orders `alsoCovers`, it is never shown.
  const alsoWorks = (c: GapCandidateView): string | null =>
    c.alsoCovers.length === 0
      ? null
      : t("progress.gapProposal.alsoWorks", {
          muscles: joinList(
            c.alsoCovers.map((a) => t(muscleKey(a.muscle))),
            locale,
          ),
          defaultValue: "also works {{muscles}}",
        });

  return (
    <View>
      <Pressable
        onPress={toggle}
        {...a11yProps}
        accessibilityRole="button"
        accessibilityLabel={t("progress.gapProposal.a11yToggle", {
          muscle: muscleLabel,
          gap,
          defaultValue: "{{muscle}}: close the gap, {{gap}} sets per week to MEV",
        })}
        style={styles.toggle}
      >
        <Text style={styles.chevron} {...hiddenFromScreenReader()}>
          {expanded ? "▾" : "▸"}
        </Text>
        <Text style={styles.toggleLbl}>
          {t("progress.gapProposal.toggle", { defaultValue: "Close the gap" })}
        </Text>
        <Text style={styles.toMev}>
          <Trans
            t={t}
            i18nKey="progress.gapProposal.toMev"
            count={proposal.gap}
            values={{ n: gap }}
            components={{ num: <Text style={styles.num} /> }}
          />
        </Text>
      </Pressable>
      {expanded ? (
        <View style={styles.panel}>
          <Text style={styles.basis}>
            {t("progress.gapProposal.basis", {
              mev: formatSets(proposal.mev),
              avg: formatSets(proposal.avgPerWeek),
              gap,
              defaultValue: "MEV {{mev}} − avg {{avg}}/wk = {{gap}}/wk",
            })}
          </Text>
          {proposal.candidates.length > 0 ? (
            <Text style={styles.basis}>
              {t("progress.gapProposal.eachAlone", {
                defaultValue: "Any one of these closes it on its own:",
              })}
            </Text>
          ) : null}
          {proposal.candidates.map((c, i) => {
            const name = pickLocalizedText(c.name, lang);
            const sets = t("progress.volumeLandmark.sets", {
              count: c.setsToClose,
              n: formatSets(c.setsToClose),
            });
            const also = alsoWorks(c);
            return (
              <View
                key={c.slug}
                accessible
                accessibilityLabel={[name, sets, also].filter(Boolean).join(", ")}
                style={[styles.cand, i > 0 ? styles.candDivider : null]}
              >
                <View style={styles.candName}>
                  <Text style={styles.name}>{name}</Text>
                  {also ? <Text style={styles.also}>{also}</Text> : null}
                </View>
                <Text style={styles.sets}>{sets}</Text>
              </View>
            );
          })}
          <Text style={styles.foot}>{footFor(t, proposal, viewer)}</Text>
        </View>
      ) : null}
    </View>
  );
}

const footFor = (
  t: TFn,
  p: Extract<RowGapProposal, { kind: "proposal" }>,
  viewer: ProgressViewer,
): string => {
  if (p.candidates.length === 0) {
    return p.place !== null && viewer === "athlete"
      ? t("progress.gapProposal.noneAtPlace", {
          place: p.place,
          defaultValue:
            "Nothing left at {{place}}: every exercise for this muscle is marked missing there.",
        })
      : t("progress.gapProposal.noneInCatalog", {
          defaultValue: "No exercise in the catalog trains this muscle.",
        });
  }
  if (viewer === "coach") return t("progress.gapProposal.wholeCatalog", {
      defaultValue:
        "Whole catalog: where the athlete trains isn't shared with coaches.",
    });
  return p.place !== null
    ? t("progress.gapProposal.atPlace", {
        place: p.place,
        defaultValue: "At {{place}}, without what you marked missing there.",
      })
    : t("progress.gapProposal.noPlace", {
        defaultValue: "Whole catalog: no default place set.",
      });
};

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    toggle: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      minHeight: 44,
      marginTop: s.s2,
    },
    chevron: { color: p.ink2, fontSize: ty.sizes.sm, width: 12 },
    toggleLbl: { color: p.ink2, fontSize: ty.sizes.base, fontWeight: "600" },
    toMev: {
      marginLeft: "auto",
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
      flexShrink: 1,
      textAlign: "right",
    },
    // A rule, not a box: no card inside the card (DESIGN.md). borderStrong
    // clears 3:1 on surface in every family (Decision 7).
    panel: {
      marginTop: s.s2,
      borderLeftWidth: 2,
      borderLeftColor: p.borderStrong,
      paddingLeft: s.s4,
      gap: s.s2,
    },
    basis: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    cand: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s3,
      paddingVertical: s.s2,
    },
    candDivider: { borderTopWidth: 1, borderTopColor: p.border },
    candName: { flexShrink: 1 },
    name: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "500" },
    also: { color: p.ink3, fontSize: ty.sizes.sm },
    sets: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.base,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    // The number carries the line, as in the mockup: mono, weight, ink.
    num: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontWeight: "600",
    },
    foot: { color: p.ink3, fontSize: ty.sizes.sm },
    note: { color: p.ink2, fontSize: ty.sizes.sm, marginTop: s.s2 },
  });
}
